import ENV from './generated/env';

import { registerMessages } from './shared/translations';
import * as i18n from './i18n';
import { MessageBroker } from './shared/message-broker';
import { komojuFetch } from './shared/komoju-api';
registerMessages(i18n);

// Language gets stored in here, mostly controlled by <komoju-fields>.
declare let window: WindowWithKomojuGlobals;

// This is the main entrypoint for implementers.
// The <komoju-fields> element manages an iframe that securely hosts the KOMOJU fields UI.
// All <komoju-fields> attributes are sent into the iframe and synced with the <komoju-host> element.
export default class KomojuFieldsElement extends HTMLElement {
  static get observedAttributes() {
    return [
      'komoju-api',
      'session',
      'session-id',
      'publishable-key',
      'payment-type',
      'locale',
      'theme',
      'token',
      'name',
    ];
  }

  // This message broker lets us communicate with the iframe using promises instead of
  // raw postMessage and addEventListener.
  broker: MessageBroker;

  // When a <komoju-fields> element appears inside of a <form> tag, we attach a submit handler to it
  // so we can seamlessly behave like an <input> tag.
  // This formSubmitHandler lets us clean up the event listener when disconnected.
  formSubmitHandler?: {
    form: HTMLFormElement,
    target: HTMLElement, // target is different from form so we can ensure our event gets called first
    handler: (event: Event) => void
  };
  _submitting: boolean = false;

  session: KomojuSession | null = null;

  dialog: DialogElement;

  get theme() {
    return this.getAttribute('theme');
  }
  set theme(value) {
    this.setAttribute('theme', value ?? '');
  }

  get komojuApi() {
    return this.getAttribute('komoju-api') ?? 'https://komoju.com';
  }
  set komojuApi(value) {
    this.setAttribute('komoju-api', value);
  }

  // To keep manual submit() calls idempotent, we hold onto the token from our last
  // (and presumably only) successful submit.
  token?: string;

  constructor() {
    super();
    this.broker = new MessageBroker();
    if (isBrokenChromeVersion()) {
      this.dialog = document.createElement('komoju-dialog') as DialogElement;
    } else {
      this.dialog = document.createElement('dialog');
      this.dialog.classList.add('komoju-fields-dialog');
    }
    this.dialog.classList.add('komoju-dialog');
    this.listenToMessagesFromIframe(this.broker);

    const styleId = 'komoju-fields-global-styles';
    if (document.getElementById(styleId)) {
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .komoju-fields-dialog {
        width: 80%;
        height: 80%;
        padding: 0;
      }
      @media (max-width: 480px) {
        .komoju-fields-dialog {
          width: 100%;
          height: 100%;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // When connected, we want to find the form that this element is in and attach a submit handler to it.
  connectedCallback() {
    const iframeParams = new URLSearchParams();
    iframeParams.append('broker', this.broker.id);
    if (this.hasAttribute('komoju-api')) {
      iframeParams.append('api', this.getAttribute('komoju-api')!);
    }

    // Set up our fields host iframe.
    const iframe = document.createElement('iframe') as HTMLIFrameElement;
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    iframe.setAttribute('allow', 'payment *');
    iframe.title = 'KOMOJU secure payment fields';
    iframe.src = `${ENV.CDN}/fields-iframe.html#${iframeParams.toString()}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.overflow = 'hidden';
    iframe.height = '50';
    iframe.addEventListener('load', () => {
      if (!iframe.contentWindow) throw new Error('KOMOJU Fields: iframe had no contentWindow');
      // Set up our message broker now that the iframe is loaded.
      this.broker.setup({
        send: iframe.contentWindow,
        receive: window,
      });
    });
    this.replaceChildren(iframe, this.dialog);

    // Crudely search for parent element until it is a form tag.
    let parent = this.parentElement;
    while (parent && parent.tagName !== 'FORM') {
      parent = parent.parentElement;
    }
    // It's OK if we can't find a parent form tag.
    // Implementers can call submit() manually on this element.
    if (!parent) return;
    const form = parent as HTMLFormElement;

    // We set the event target to the form's parent to ensure that our handler is called first.
    // This works because of capturing. Capturing events are called on parents *before* target.
    const target = form.parentElement;
    if (!target) return;

    const handler = (event: Event) => {
      if (this._submitting) return;
      // Make sure this komoju-fields element is visible.
      if (this.offsetParent === null) return;
      // Make sure this event is for the right form element.
      if (event.target !== form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.submit(event);
    };
    target.addEventListener('submit', handler, true);
    this.formSubmitHandler = { form, target, handler };
  }

  // When disconnected, we want to remove the submit handler from the form (if added).
  disconnectedCallback() {
    if (this.formSubmitHandler) {
      this.formSubmitHandler.target.removeEventListener('submit', this.formSubmitHandler.handler, true);
      this.formSubmitHandler = undefined;
    }

    this.broker.destroy();
  }

  // Receive messages from the iframe.
  listenToMessagesFromIframe(broker: MessageBroker) {
    // The iframe will sometimes ask us to dispatch an event.
    broker.receive<KomojuDispatchEvent>('dispatch-event', (message) => {
      const event = new CustomEvent(message.name, {
        detail: message.detail,
        bubbles: true,
        composed: true,
        cancelable: true,
      });

      // Sync own session with iframe session.
      // This way implementers can always access the latest session from the komoju-fields element
      // in case they want to know what payment methods are supported, etc.
      if (message.name === 'komoju-session-change') {
        this.session = message.detail.session as KomojuSession;
      }

      const result: KomojuDispatchResult = {
        type: 'dispatch-result',
        cancel: !this.dispatchEvent(event)
      }

      return result;
    });

    // The iframe tells us how much height its content has so that we can resize it seamlessly.
    broker.receive<KomojuMessageResize>('resize', (message) => {
      const iframe = this.querySelector('iframe') as HTMLIFrameElement;
      iframe.height = message.height;
    });

    broker.receive<KomojuDialogStart>('dialog-start', async (message) => {
      const result: KomojuDialogResult = {
        type: 'dialog-result',
        result: await this.show3DSDialog(message.url),
      };
      return result;
    });
  }

  // Reactive attribute handling. Send all attrs to the iframe so that they are synced with the komoju-host element.
  async attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null) {
    this.broker.send<KomojuMessageAttr>({
      type: 'attr',
      attr: name,
      value: newValue,
    });
  }

  // Consumers may call this to submit the form.
  // If the 'token' attribute is set, this will return the token response from KOMOJU.
  async submit(event?: Event): Promise<KomojuToken | KomojuSecureToken | void> {
    if (this.token) return JSON.parse(this.token);

    // Ask the iframe to submit.
    const submitResult = await this.broker.send({ type: 'submit' });
    if (submitResult?.type !== 'submit-result') {
      throw new Error(`Unexpected submit response from komoju-fields iframe ${JSON.stringify(submitResult)}`);
    }
    const result = submitResult as KomojuSubmitResult;

    // On error, emit a komoju-invalid event in case consumers want to handle it.
    if (result.errors) {
      this.dispatchEvent(new CustomEvent('komoju-invalid', {
        detail: { errors: result.errors }, bubbles: true, composed: true,
      }));
      return;
    }

    // On payment success, redirect.
    if (result.pay) {
      if (!result.pay.error) await this.handlePayResult(result.pay);
      return;
    }

    // If this is part of a form submit, we want to add the token to the form and resubmit.
    if (result.token && event && this.formSubmitHandler) {
      const form = this.formSubmitHandler.form;

      // Now we add an input to the form with the token and submit it.
      const inputName = this.getAttribute('name') ?? 'komoju_token';
      let input = document.querySelector(`input[name="${inputName}"]`) as HTMLInputElement | null;
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        form.append(input);
      }
      input.value = result.token.id;

      // Re-submit the form without our submit event getting in the way.
      this.submitParentForm();
      return;
    }

    // Token submission.
    if (result.token) {
      this.token = JSON.stringify(result.token);
      return result.token;
    }

    throw new Error("KOMOJU Fields bug: submit result was not handled");
  }

  // After submit in a standard integration, we will usually just redirect.
  // But there's a special case where we show instructions, e.g. for konbini and bank transfer.
  async handlePayResult(payResult: KomojuPayResult) {
    const session = this.session;
    if (!session) throw new Error("handlePayResult called without a session");

    const instructions = payResult.payment?.payment_details?.instructions_url;

    if (instructions) {
      const returnURL = new URL(session.return_url ?? session.session_url);
      returnURL.searchParams.append('session_id', session.id);
      this.showInstructionsDialog(instructions, returnURL.toString());
    } else if (payResult.redirect_url) {
      window.location.href = payResult.redirect_url;
    } else {
      throw new Error(`payResult should have a redirect_url but doesnt ${JSON.stringify(payResult)}`);
    }
  }

  // Removes our form submit override and submits the parent form directly.
  async submitParentForm() {
    if (!this.formSubmitHandler) throw new Error('KOMOJU Fields: tried to submit nonexistent parent form');
    const form = this.formSubmitHandler.form;

    // Re-submit the form without our submit event getting in the way.
    try {
      // This _submitting flag should prevent infinite recursion.
      this._submitting = true;
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      if (form.dispatchEvent(submitEvent)) {
        form.submit();
      } else {
        this.broker.send({ type: 'end-fade' });
      }
    } finally {
      this._submitting = false;
    }
  }

  showInstructionsDialog(url: string, finishURL: string) {
    const dialog = this.dialog;

    const iframe = createIframe(url);
    iframe.style.height = '90%';

    const closeButton = document.createElement('a');
    const closeText = document.createElement('komoju-i18n') as KomojuI18nElement;
    closeText.key = 'close';
    closeButton.append(closeText);
    closeButton.classList.add('komoju-fields-close-dialog');
    closeButton.href = finishURL;
    closeButton.style.display = 'block';
    closeButton.style.padding = '10px';

    dialog.replaceChildren(
      closeButton,
      iframe,
    );
    dialog.showModal();
  }

  show3DSDialog(url: string): Promise<KomojuSecureTokenOrError> {
    return new Promise((resolve, reject) => {
      const dialog = this.dialog;
      const origin = ENV['CDN'];

      const iframe = createIframe(url);

      window.addEventListener('message', async (event) => {
        if (event.origin !== origin) return;

        try {
          if (!event.data?._komojuFields) return;

          const { secureTokenId } = event.data;
          if (!secureTokenId) throw new Error('No secureTokenId in message');

          const credentials = {
            komojuApi: this.komojuApi,
            publishableKey: this.getAttribute('publishable-key') ?? '',
          };
          const secureTokenResponse = await komojuFetch(credentials, 'GET', `/api/v1/secure_tokens/${secureTokenId}`);
          if (secureTokenResponse.status >= 400) {
            const error = await secureTokenResponse.json() as KomojuApiError;
            resolve({ error });
            return;
          }
          const secureToken = await secureTokenResponse.json() as KomojuSecureToken;

          dialog.close();
          resolve({ secureToken });
        } catch (e) {
          reject(e);
        }
      });

      dialog.replaceChildren(iframe);
      dialog.showModal();
    });
  }
}

// Set up dynamic properties for each observed attribute, to support e.g.
//   const fields = document.querySelector('komoju-fields');
//   fields.sessionId = 'abc123';
for (const attr of KomojuFieldsElement.observedAttributes) {
  // A few special cases here.
  if (attr === 'session' || attr === 'theme' || attr === 'komoju-api') continue;

  Object.defineProperty(KomojuFieldsElement.prototype, camelCase(attr), {
    get() {
      return this.getAttribute(attr);
    },
    set(value) {
      if (value === null) this.removeAttribute(attr);
      else this.setAttribute(attr, value);
    },
  });
}

function camelCase(str: string) {
  return str
    .split('-')
    .reduce((a, b) => a + b.charAt(0).toUpperCase() + b.slice(1));
}

function createIframe(url: string): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-forms allow-same-origin'
  );
  iframe.src = url;
  iframe.style.border = 'none';
  iframe.style.width = '100%';
  iframe.style.height = '100%';
  return iframe;
}

function isBrokenChromeVersion(): boolean {
  if (location.hash == "#brokenchrome") return true;

  const myUa = navigator.userAgent;

  const isAndroid = /Android/.test(myUa);
  if (!isAndroid) return false;

  const versionMatch = myUa.match(/Chrome\/(\d+)/);
  if (!versionMatch) return false;

  const version = versionMatch[1];
  if (!version) return false;

  return parseInt(version, 10) >= 127;
}
